/** esl */ /* eslint-disable max-lines */
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { B2CHalfCardConfig } from "@payment-ui/interfaces/BillToCompany";
import { isEmpty } from "@swiggy-private/js-utils";
import { DropdownField, InputField, SuggestiveTypingField, MultiSuggestiveTypingField } from "../fields";
import {
    getSortedOptions,
    filterStaticOptions,
    extractSuggestion,
    isChipDuplicate,
    getSubmitData as formatSubmitData,
    getInitialValues,
    getOrCreateInputRef,
} from "../helpers/fieldHelper";
import { EMAIL_SUGGESTION_API_CALL, FIELD_TIMING_CONSTANTS, FIELD_TYPES } from "@payment-ui/helpers/FieldRenderer";
import type { ExpenseCodeCacheData } from "../../../interfaces/ExpenseCodeTypes";
import type {
    IFieldOption,
    IApiCallHandlers,
    IDropdownFieldConfig,
    IInputFieldConfig,
    ISuggestiveTypingFieldConfig,
    IMultiSuggestiveTypingFieldConfig,
} from "../Interface/fieldRendererInterface";
import { EXPENSE_CODE_HALF_CARD_TEXTS } from "../constants";
import { EmptyResultsText } from "../styles";
import OptionText from "../OptionText";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

export const useFieldRenderer = (
    b2CHalfCardConfig: B2CHalfCardConfig,
    apiCallHandlers?: IApiCallHandlers,
    isDetailsSubmitted?: boolean,
    cachedData?: ExpenseCodeCacheData | null,
) => {
    const fields = b2CHalfCardConfig?.fields;
    const initialValues = useMemo(() => getInitialValues(fields, cachedData), [fields, cachedData]);
    const [fieldValues, setFieldValues] = useState<Record<string, string>>(initialValues.fieldValues);
    const [optionValues, setOptionValues] = useState<Record<string, IFieldOption[]>>({});
    const [chipsValues, setChipsValues] = useState<Record<string, string[]>>(initialValues.chipsValues);
    const [chipUniqueIds, setChipUniqueIds] = useState<Record<string, string[]>>(initialValues.chipUniqueIds);
    const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});

    useEffect(() => {
        setFieldValues(initialValues.fieldValues);
        setChipsValues(initialValues.chipsValues);
        setChipUniqueIds(initialValues.chipUniqueIds);
        setOptionValues({});
        setLoadingFields({});
        setTouchedFields({});
    }, [initialValues]);
    const inputRefs = useRef<Record<string, React.RefObject<HTMLInputElement>>>({});
    const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
    const latestEmailRequest = useRef<Record<string, number>>({});
    const isSelectingOption = useRef<Record<string, boolean>>({});
    const fieldValuesRef = useRef(fieldValues);
    const optionValuesRef = useRef(optionValues);

    // Keep refs in sync with state
    useEffect(() => {
        fieldValuesRef.current = fieldValues;
        optionValuesRef.current = optionValues;
    }, [fieldValues, optionValues]);

    // Cleanup all timers on unmount
    useEffect(() => {
        const debounceTimersRef = debounceTimers.current;

        return () => {
            Object.values(debounceTimersRef).forEach((timer) => clearTimeout(timer));
        };
    }, []);

    const handleFieldChange = useCallback((fieldId: string, value: string) => {
        setFieldValues((prev) => ({
            ...prev,
            [fieldId]: value,
        }));
    }, []);

    const handleEmailAutoSuggest = useCallback(
        async (fieldId: string, searchValue: string) => {
            const requestId = (latestEmailRequest.current[fieldId] ?? 0) + 1;
            latestEmailRequest.current[fieldId] = requestId;

            // Clear any existing timer for this field
            if (debounceTimers.current[fieldId]) {
                clearTimeout(debounceTimers.current[fieldId]);
            }

            // If empty search, clear options
            if (!searchValue || searchValue.trim().length === 0) {
                setOptionValues((prev) => ({ ...prev, [fieldId]: [] }));
                setLoadingFields((prev) => ({ ...prev, [fieldId]: false }));
                return;
            }

            // Check if email API handler is provided
            if (!apiCallHandlers?.email) {
                return;
            }

            // Set loading state immediately
            setLoadingFields((prev) => ({ ...prev, [fieldId]: true }));

            // Debounce the API call
            debounceTimers.current[fieldId] = setTimeout(async () => {
                try {
                    const emailHandler = apiCallHandlers.email;
                    if (emailHandler) {
                        const options = await emailHandler(searchValue.trim());
                        if (latestEmailRequest.current[fieldId] !== requestId) return;
                        setOptionValues((prev) => ({ ...prev, [fieldId]: options }));
                        setLoadingFields((prev) => ({ ...prev, [fieldId]: false }));
                    }
                } catch (error) {
                    if (latestEmailRequest.current[fieldId] !== requestId) return;
                    setOptionValues((prev) => ({ ...prev, [fieldId]: [] }));
                    setLoadingFields((prev) => ({ ...prev, [fieldId]: false }));
                }
            }, FIELD_TIMING_CONSTANTS.DEBOUNCE_DELAY_MS);
        },
        [apiCallHandlers],
    );

    const handleSuggestiveTypingChange = useCallback(
        (fieldId: string, value: string, allOptions: IFieldOption[], apiCall?: string) => {
            handleFieldChange(fieldId, value);

            // Mark field as touched when user types
            setTouchedFields((prev) => {
                if (!prev[fieldId]) {
                    return { ...prev, [fieldId]: true };
                }
                return prev;
            });

            // If API call is configured, use API instead of static options
            if (apiCall) {
                if (apiCall === EMAIL_SUGGESTION_API_CALL) {
                    handleEmailAutoSuggest(fieldId, value);
                }
                return;
            }

            // Handle static options filtering
            const filtered = filterStaticOptions(value, allOptions);
            setOptionValues((prev) => ({
                ...prev,
                [fieldId]: filtered,
            }));
        },
        [handleFieldChange, handleEmailAutoSuggest],
    );

    const handleSuggestiveTypingClick = useCallback(
        (fieldId: string, data?: Record<string, unknown>) => {
            isSelectingOption.current[fieldId] = true;

            const suggestion = extractSuggestion(data);

            if (suggestion) {
                handleFieldChange(fieldId, suggestion);
                setOptionValues((prev) => ({
                    ...prev,
                    [fieldId]: [],
                }));
            }

            setTimeout(() => {
                isSelectingOption.current[fieldId] = false;
            }, FIELD_TIMING_CONSTANTS.OPTION_SELECT_RESET_DELAY_MS);
        },
        [handleFieldChange],
    );

    useEffect(() => {
        const suggestiveFields = fields?.filter((f) => f?.type === FIELD_TYPES.FREE_TEXT) || [];
        const multiSuggestiveFields = fields?.filter((f) => f?.type === FIELD_TYPES.MULTI_SUGGESTIVE_TYPING) || [];

        // Early return if no suggestive typing fields
        if (suggestiveFields.length === 0 && multiSuggestiveFields.length === 0) return;

        const cleanupFunctions: Array<() => void> = [];

        // Handle single suggestive typing fields
        suggestiveFields.forEach((field) => {
            if (!field) return;

            const fieldId = field.id;
            const inputRef = inputRefs.current[fieldId];
            const inputElement = inputRef?.current;

            if (!inputElement) return;

            const allOptions = field.options || [];

            const handleClick = () => {
                // Show all options on click - only for suggestiveTyping fields
                if (allOptions.length > 0) {
                    setOptionValues((prev) => ({
                        ...prev,
                        [fieldId]: allOptions,
                    }));
                }
            };

            const handleBlur = () => {
                // Don't clear if we're currently selecting an option
                if (isSelectingOption.current[fieldId]) {
                    return;
                }

                // Use refs to get latest state values without adding to dependencies
                const currentFieldValue = fieldValuesRef.current[fieldId] || "";

                if (!field.apiCall) {
                    const sortedOriginal = getSortedOptions(currentFieldValue, allOptions);

                    // Clear input if there are no valid matches even in the original list
                    if (isEmpty(sortedOriginal)) {
                        handleFieldChange(fieldId, "");
                        setOptionValues((prev) => ({
                            ...prev,
                            [fieldId]: [],
                        }));
                    }
                }

                // Always clear options dropdown with delay to allow click to complete
                setTimeout(() => {
                    setOptionValues((prev) => ({
                        ...prev,
                        [fieldId]: [],
                    }));
                }, FIELD_TIMING_CONSTANTS.BLUR_DELAY_MS);
            };

            inputElement.addEventListener("click", handleClick);
            inputElement.addEventListener("blur", handleBlur);

            cleanupFunctions.push(() => {
                inputElement.removeEventListener("click", handleClick);
                inputElement.removeEventListener("blur", handleBlur);
            });
        });

        multiSuggestiveFields.forEach((field) => {
            if (!field) return;

            const fieldId = field.id;
            let currentInputElement: HTMLInputElement | null = null;

            const handleBlur = () => {
                setTimeout(() => {
                    if (isSelectingOption.current[fieldId]) {
                        return;
                    }
                    if (debounceTimers.current[fieldId]) {
                        clearTimeout(debounceTimers.current[fieldId]);
                        delete debounceTimers.current[fieldId];
                        setLoadingFields((prev) => ({ ...prev, [fieldId]: false }));
                    }

                    handleFieldChange(fieldId, "");
                    setOptionValues((prev) => ({
                        ...prev,
                        [fieldId]: [],
                    }));
                }, FIELD_TIMING_CONSTANTS.MULTI_BLUR_DELAY_MS);
            };

            const attachListeners = (element: HTMLInputElement) => {
                if (currentInputElement === element) {
                    return;
                }

                if (currentInputElement) {
                    currentInputElement.removeEventListener("blur", handleBlur);
                }

                element.addEventListener("blur", handleBlur);
                currentInputElement = element;
            };

            // Use MutationObserver to detect when the input element changes
            const observer = new MutationObserver(() => {
                const newElement = inputRefs.current[fieldId]?.current;
                if (newElement && newElement !== currentInputElement) {
                    attachListeners(newElement);
                }
            });

            const setupTimeout = setTimeout(() => {
                const inputRef = inputRefs.current[fieldId];
                const inputElement = inputRef?.current;

                if (inputElement) {
                    attachListeners(inputElement);
                    // Start observing the parent element for changes
                    const parentElement = inputElement.parentElement;
                    if (parentElement) {
                        observer.observe(parentElement, {
                            childList: true,
                            subtree: true,
                        });
                    }
                }
            }, FIELD_TIMING_CONSTANTS.MULTI_FIELD_SETUP_DELAY_MS);

            cleanupFunctions.push(() => {
                clearTimeout(setupTimeout);
                observer.disconnect();
                if (currentInputElement) {
                    currentInputElement.removeEventListener("blur", handleBlur);
                }
            });
        });

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, [fields, handleFieldChange]);

    const handleChipRemove = useCallback((fieldId: string, index: number) => {
        setChipsValues((prev) => ({
            ...prev,
            [fieldId]: prev[fieldId].filter((_, i) => i !== index),
        }));
        setChipUniqueIds((prev) => ({
            ...prev,
            [fieldId]: (prev[fieldId] || []).filter((_, i) => i !== index),
        }));
    }, []);

    const handleChipsChange = useCallback((fieldId: string, newChips: string[]) => {
        setChipsValues((prev) => {
            const oldChips = prev[fieldId] || [];
            if (oldChips.length > newChips.length) {
                const removedIndices = oldChips.reduce<number[]>((acc, chip, i) => {
                    if (!newChips.includes(chip)) acc.push(i);
                    return acc;
                }, []);
                setChipUniqueIds((prevIds) => ({
                    ...prevIds,
                    [fieldId]: (prevIds[fieldId] || []).filter((_, i) => !removedIndices.includes(i)),
                }));
            }
            return { ...prev, [fieldId]: newChips };
        });
    }, []);

    const handleMultiSuggestiveClick = useCallback(
        (fieldId: string, data?: Record<string, unknown>) => {
            // Set flag to prevent blur from interfering with selection
            isSelectingOption.current[fieldId] = true;

            const suggestion = extractSuggestion(data);

            const uniqueId = data?.uniqueId as string | undefined;

            if (suggestion) {
                setChipsValues((prev) => {
                    const latestChips = prev[fieldId] || [];

                    if (isChipDuplicate(latestChips, suggestion)) {
                        return prev;
                    }

                    if (uniqueId) {
                        setChipUniqueIds((prevIds) => {
                            const latestIds = prevIds[fieldId] || [];
                            if (latestIds.includes(uniqueId)) {
                                return prevIds;
                            }
                            return { ...prevIds, [fieldId]: [...latestIds, uniqueId] };
                        });
                    }

                    return { ...prev, [fieldId]: [...latestChips, suggestion] };
                });
            }

            handleFieldChange(fieldId, "");
            setOptionValues((prev) => ({ ...prev, [fieldId]: [] }));

            setTimeout(() => {
                isSelectingOption.current[fieldId] = false;
            }, FIELD_TIMING_CONSTANTS.OPTION_SELECT_RESET_DELAY_MS);
        },
        [handleFieldChange],
    );

    const renderField = useMemo(() => {
        const renderDropdownFieldMemo = (field: IDropdownFieldConfig) => (
            <DropdownField
                key={field.id}
                id={field.id}
                label={field.label}
                enabled={field.enabled}
                options={field.options}
                value={fieldValues[field.id]}
                onChange={handleFieldChange}
                isDetailsSubmitted={isDetailsSubmitted}
            />
        );

        const renderInputFieldMemo = (field: IInputFieldConfig) => (
            <InputField
                key={field.id}
                id={field.id}
                label={field.label}
                enabled={field.enabled}
                value={fieldValues[field.id]}
                onChange={handleFieldChange}
                isDetailsSubmitted={isDetailsSubmitted}
            />
        );

        const renderSuggestiveTypingFieldMemo = (field: ISuggestiveTypingFieldConfig) => {
            const fieldId = field.id;
            const currentFieldValue = fieldValues[fieldId] || "";
            const currentOptions = optionValues[fieldId] || [];
            const allOptions = field.options || [];
            const isLoading = loadingFields[fieldId] || false;

            const inputRef = getOrCreateInputRef(fieldId, inputRefs.current);
            const sortedOptions = getSortedOptions(currentFieldValue, currentOptions);

            const formattedOptions = sortedOptions.map((option) => ({
                ...option,
                text: <OptionText matchStr={currentFieldValue} fullStr={option.data.value} />,
                ...(option.secondaryText && {
                    secondaryText: (
                        <Text maxLines={1} variant="V2.body1.neutral.regular">
                            {option.secondaryText}
                        </Text>
                    ),
                }),
            }));

            const emptyResultsContent =
                isEmpty(sortedOptions) && !isEmpty(currentOptions) ? (
                    <EmptyResultsText
                        variant="V2.body1.neutral.medium"
                        color="text_High_Emphasis"
                        data-testid={`${fieldId}-empty-results-text`}
                    >
                        {EXPENSE_CODE_HALF_CARD_TEXTS.NO_RESULT_FOUND}
                    </EmptyResultsText>
                ) : null;

            return (
                <SuggestiveTypingField
                    key={fieldId}
                    id={fieldId}
                    label={field.label}
                    enabled={field.enabled}
                    value={currentFieldValue}
                    sortedOptions={formattedOptions}
                    isLoading={isLoading}
                    inputRef={inputRef}
                    allOptions={allOptions}
                    apiCall={field.apiCall}
                    onChangeValue={handleSuggestiveTypingChange}
                    onClick={handleSuggestiveTypingClick}
                    isDetailsSubmitted={isDetailsSubmitted}
                    emptyResultsContent={emptyResultsContent}
                />
            );
        };

        const renderMultiSuggestiveTypingFieldMemo = (field: IMultiSuggestiveTypingFieldConfig) => {
            const fieldId = field.id;
            const currentFieldValue = fieldValues[fieldId] || "";
            const currentOptions = optionValues[fieldId] || [];
            const currentChips = chipsValues[fieldId] || [];
            const allOptions = field.options || [];
            const isLoading = loadingFields[fieldId] || false;
            const isFieldTouched = touchedFields[fieldId] || false;

            const inputRef = getOrCreateInputRef(fieldId, inputRefs.current);
            const sortedOptions = getSortedOptions(currentFieldValue, currentOptions);

            const formattedOptions = sortedOptions.map((option) => ({
                ...option,
                text: <OptionText matchStr={currentFieldValue} fullStr={option.data.value} />,
                ...(option.secondaryText && {
                    secondaryText: (
                        <Text maxLines={1} variant="V2.body3.neutral.medium" color="text_Low_Emphasis">
                            {option.secondaryText}
                        </Text>
                    ),
                }),
            }));

            return (
                <MultiSuggestiveTypingField
                    key={fieldId}
                    id={fieldId}
                    label={field.label}
                    enabled={field.enabled}
                    value={currentFieldValue}
                    chips={currentChips}
                    sortedOptions={formattedOptions}
                    isLoading={isLoading}
                    isFieldTouched={isFieldTouched}
                    inputRef={inputRef}
                    allOptions={allOptions}
                    apiCall={field.apiCall}
                    onChangeValue={handleSuggestiveTypingChange}
                    onClick={handleMultiSuggestiveClick}
                    onChipRemove={handleChipRemove}
                    onChipsChange={handleChipsChange}
                    isDetailsSubmitted={isDetailsSubmitted}
                />
            );
        };

        return (
            fields?.map((field) => {
                if (!field) return null;

                switch (field.type) {
                    case FIELD_TYPES.DROPDOWN:
                        return renderDropdownFieldMemo(field as IDropdownFieldConfig);

                    case FIELD_TYPES.INPUT:
                        return renderInputFieldMemo(field as IInputFieldConfig);

                    case FIELD_TYPES.FREE_TEXT:
                        return renderSuggestiveTypingFieldMemo(field as ISuggestiveTypingFieldConfig);

                    case FIELD_TYPES.MULTI_SUGGESTIVE_TYPING:
                        return renderMultiSuggestiveTypingFieldMemo(field as IMultiSuggestiveTypingFieldConfig);

                    default:
                        return null;
                }
            }) ?? []
        );
    }, [
        fields,
        fieldValues,
        optionValues,
        chipsValues,
        loadingFields,
        touchedFields,
        handleFieldChange,
        handleSuggestiveTypingChange,
        handleSuggestiveTypingClick,
        handleChipRemove,
        handleChipsChange,
        handleMultiSuggestiveClick,
        isDetailsSubmitted,
    ]);

    const getSubmitData = useCallback(() => {
        return formatSubmitData(fieldValues, chipsValues, chipUniqueIds, fields);
    }, [fieldValues, chipsValues, chipUniqueIds, fields]);

    // Validate if all required fields are filled
    const isFormValid = useMemo(() => {
        if (!fields || fields.length === 0) {
            return true; // No fields to validate
        }

        return fields.every((field) => {
            if (!field || !field.required || field.enabled === false) {
                return true; // Field is not required, so it's valid
            }

            const fieldId = field.id;

            // For multiSuggestiveTyping fields, check if chips array has values
            if (field.type === FIELD_TYPES.MULTI_SUGGESTIVE_TYPING) {
                const chips = chipsValues[fieldId] || [];
                return chips.length > 0;
            }

            // For all other field types, check if fieldValue is not empty
            const value = fieldValues[fieldId];
            return !isEmpty(value);
        });
    }, [fields, fieldValues, chipsValues]);

    return {
        renderField,
        fieldValues,
        chipsValues,
        getSubmitData,
        isFormValid,
    };
};
